import {
  type DocumentMeta,
  getEnrichedSearchDocuments,
  makeSearchFullTextDocumentsQueryString,
  searchFullTextDocuments
} from "@prismicio/editor-fields"
import { useDebounce, useStableCallback } from "@prismicio/editor-support/React"
import { createContext, useContext, useRef, useState } from "react"

import { staticEditorConfig } from "../../app/editorConfig"
import { trackDocumentSearchInDialog } from "../../app/tracking"
import type { Filter } from "../DocumentListBody/ActionBar/useActionBar"
import { useDocumentListLocales } from "../useDocumentListLocales"

export interface SearchDialogContext {
  filters: {
    searchTerm: string
    customType: Filter[]
    tag: Filter[]
    hasFiltersApplied: boolean
  }
  loading: boolean
  setLoading: (state: boolean) => void
}

export const SearchDialogContext = createContext<SearchDialogContext>({
  loading: false,
  setLoading: () => null,
  filters: {
    searchTerm: "",
    customType: [],
    tag: [],
    hasFiltersApplied: false
  }
})

export function useSearchDialogContext() {
  return useContext(SearchDialogContext)
}

const searchDebounceMs = 300

export function useDocumentSearch() {
  const { filters, setLoading } = useSearchDialogContext()
  const [documents, setDocuments] = useState<DocumentMeta[]>([])
  const { locale } = useDocumentListLocales()

  const { documentSearchBaseUrl, coreApiBaseUrl, authStrategy, repository } =
    staticEditorConfig

  const abortControllerRef = useRef<AbortController | null>(null)

  useDebounce(
    filters,
    searchDebounceMs,
    useStableCallback(async (filters) => {
      if (!filters.hasFiltersApplied) {
        setDocuments([])
        setLoading(false)

        return
      }

      // Cancel the previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create a new AbortController for the new request
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      const { searchTerm, customType, tag } = filters

      const queryString = makeSearchFullTextDocumentsQueryString({
        repository,
        searchTerm,
        customTypesIds: customType.map((type) => type.value),
        tags: tag.map((tag) => tag.value),
        // NOTE: locale is an implict filter, as it cannot change during the utilization of the search
        localeId: locale
      })

      try {
        const startTime = new Date().getTime()
        const response = await searchFullTextDocuments(
          documentSearchBaseUrl,
          authStrategy,
          queryString,
          { signal: abortController.signal }
        )

        const enrichedDocuments = await getEnrichedSearchDocuments(
          coreApiBaseUrl,
          authStrategy,
          response.results
        )

        // NOTE: We don't want to list any archived document here
        const nonArchivedEnrichedDocuments = enrichedDocuments.filter(
          (document) =>
            document.versions.some(({ status }) => status !== "archived")
        )

        const endTime = new Date().getTime()
        const duration = endTime - startTime
        trackDocumentSearchInDialog({
          numberOfResults: nonArchivedEnrichedDocuments.length,
          searchQuery: searchTerm,
          numberOfWords: searchTerm.split(" ").length,
          timeToGetResultsInMs: duration
        })

        setDocuments(nonArchivedEnrichedDocuments)
      } catch (err) {
        // Don't log error when request was aborted
        if (abortController.signal.aborted) return

        console.error(err)
        throw err
      } finally {
        // Don't turn off loading when request was aborted, latest request will do it
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    })
  )

  return {
    data: documents
  }
}
